<template>
  <div v-if="workspace.org || workspace.tenant">
    <SCADropdown
      v-model:is-open="isOpen"
      :class="$style.dropdown"
      :offset-y="offsetY"
      :offset-x="offsetX"
      no-border
      no-padding
      shadow-variant="upward"
    >
      <template #trigger="{ target }">
        <SCAWorkspaceItem
          :class="$style.trigger"
          :popovertarget="target"
          :title="displayTitle"
          :subtitle="displaySubtitle"
          :icon="currentIcon"
          :expandable="isExpandable"
          :expanded="isOpen"
          :focused="isOpen"
          @mouseenter="handleMouseEnter"
          @mouseleave="handleMouseLeave"
        />
      </template>
      <template #default>
        <div :class="$style.content">
          <template v-for="(tenant, index) in tenantsFiltered" :key="tenant.id">
            <div :class="$style.tenantWrapper">
              <TenantTreeItem
                :selected-org="workspace.orgId"
                :tenant="tenant"
                :org-tree="getOrgTreeForTenant(tenant, workspace.tenantOrgTrees)"
                :expand-all="tenant.id === workspace.tenantId"
                @close="isOpen = false"
                @update:selected-org="handleOrgSelect($event, tenant.id)"
              />
            </div>
            <SCADivider v-if="index < (tenantsFiltered?.length ?? 0) - 1" direction="horizontal" appearance="border" />
          </template>
        </div>
      </template>
    </SCADropdown>
  </div>
</template>
<script setup lang="ts">
import { SCADivider, SCADropdown, SCAWorkspaceItem } from "@scayla/components";
import { useScaBreakpoints } from "@scayla/core";
import { useMediaQuery } from "@vueuse/core";
import { computed, ref } from "vue";
import { useRouter } from "vue-router";

import TenantTreeItem from "@/components/navigation/tenantOrgaSelect/TenantTreeItem.vue";
import { Routes } from "@/router";
import { useWorkspace } from "@/store/workspace";

import { getDisplayTitle, getIcon, getLabel, getOrgTreeForTenant } from "./tenantOrgaSelect/tenantOrgaUtils";

const workspace = useWorkspace();
const router = useRouter();

const { isMobile } = useScaBreakpoints();
const isHovered = ref(false);
const isOpen = ref(false);

const hasHoverCapability = useMediaQuery("(hover: hover)");

const isExpandable = computed(() => isOpen.value || (hasHoverCapability.value && isHovered.value));

function handleMouseEnter() {
  if (hasHoverCapability.value) {
    isHovered.value = true;
  }
}

function handleMouseLeave() {
  if (hasHoverCapability.value) {
    isHovered.value = false;
  }
}

const offsetY = computed(() => (isMobile.value ? 0 : 12));
const offsetX = -8;

const tenantsFiltered = computed(() => {
  const tree = workspace.tenantOrgTrees;
  return workspace.tenants?.filter((t) => {
    const children = tree.get(t.id)?.children ?? [];
    if (children.length > 0) return true;
    if (!tree.has(t.id) || !t.organization) return false;
    return !!t.organization.name?.trim();
  });
});

const orgTree = computed(() => getOrgTreeForTenant(workspace.tenant, workspace.tenantOrgTrees));
const isTenantSelected = computed(() => !!workspace.tenantId && !workspace.orgId);

const currentIcon = computed(() => getIcon(isTenantSelected.value, workspace.tenant, workspace.org));
const displaySubtitle = computed(() => {
  const isActuallyTenant = !workspace.orgId;
  return getLabel(isActuallyTenant, workspace.tenant, isActuallyTenant ? null : workspace.org);
});
const displayTitle = computed(() =>
  getDisplayTitle(isTenantSelected.value, orgTree.value, workspace.tenant, workspace.org, workspace.orgId),
);

const handleOrgSelect = async (orgId: string | undefined, tenantId: string) => {
  const tenantChanged = tenantId !== workspace.tenantId;
  await workspace.setTenantId(tenantId);
  workspace.orgId = orgId;
  isOpen.value = false;
  if (tenantChanged) {
    await router.push({ name: Routes.HOME, query: { tenant: tenantId } });
  }
};
</script>
<style lang="scss" module>
.dropdown {
  display: flex;
}

.content {
  display: flex;
  flex-direction: column;
  gap: var(--space-component-gap-medium);
  width: 304px;
  max-height: calc(
    100vh - var(--sca-main-header-height) - var(--sca-sub-header-height) - var(--space-component-padding-medium) * 3
  );
  overflow-x: hidden;
  scrollbar-width: auto;
}

.trigger {
  flex-grow: 1;

  .label {
    display: flex;
    align-items: center;
    gap: var(--space-component-gap-medium);
  }

  .avatar {
    display: none;
    @screen sm {
      display: flex;
    }
  }
}
</style>
